import { US_EASTERN_TIMEZONE } from './us-eastern-day';
import type { DailyRevealPropOperator } from './daily-reveal';

/**
 * NBA Daily Reveal Configuration
 *
 * Single source of truth for the NBA *prop-based* Daily Reveal mode. Activated only when a
 * Daily Reveal contest has `sport = 'NBA'` AND its resolved mode is `prop_based`.
 *
 * HOW THIS DIFFERS FROM NFL, and why it is a separate file rather than a parameter on the NFL
 * one: the NFL week is built around three fixture windows (TNF / Sunday slate / MNF), so an NFL
 * reveal day routinely picks on games played on a DIFFERENT calendar date, and the whole
 * `resolveNflFixtureDate` / `gameWindow` apparatus exists to map one to the other. The NBA plays
 * every night, so each reveal day picks on ITS OWN date's games — there is no window to resolve
 * and no date shifting. `DailyRevealFixtureSyncService.resolveFixtureFetchDate` already returns
 * the day's own game date for any non-NFL league, so NBA needs no counterpart to that logic.
 *
 * IMPORTANT: rules and defaults only. No Sportsbook API / odds-feed client logic belongs here.
 * Prop markets are sourced through the provider architecture and frozen into the
 * `daily_reveal_prop_markets` table; the rest of the game consumes only those frozen records.
 */

/** Days of the NBA Daily Reveal week (Monday → Sunday). */
export type NBAWeekDay =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Configuration for a single day/category within the NBA week. */
export interface NBADailyRevealDayConfig {
  /** 1-based day number within the contest (1 = Monday … 7 = Sunday). */
  dayNumber: number;
  dayOfWeek: NBAWeekDay;
  /** Stable machine key, e.g. `points_king`. */
  categoryKey: string;
  /** Display name, e.g. `Points King`. */
  categoryName: string;
  /** Internal prop stat type, e.g. `points`. */
  propStatType: string;
  /** Grading operator for this category's props. */
  operator: DailyRevealPropOperator;
  /** Human-friendly default threshold label used before markets are frozen. */
  defaultThresholdLabel: string;
  /** Reveal window open time in ET (24h `HH:mm`). */
  openTimeEt: string;
}

export interface NBADailyRevealConfig {
  sport: 'NBA';
  mode: 'prop_based';
  packSize: number;
  weekStartsOn: NBAWeekDay;
  timezone: string;
  allowPickChangeBeforeLock: boolean;
  fallbackManualPropsEnabled: boolean;
  days: NBADailyRevealDayConfig[];
}

/**
 * Every NBA day closes at the tip-off of the first game it is picking on, so every day carries
 * the `first_relevant_game_kickoff` lock rule. This is the platform default
 * (`DailyRevealFixtureSyncService.DEFAULT_CLOSE_RULE`), so the days below leave `lockRule`
 * unset and inherit it rather than restating it seven times.
 *
 * NFL is the exception that needs per-day rules, because several of its days pick on the same
 * later fixture and must close at 23:59 on their OWN date instead.
 */
export const NBA_DAILY_REVEAL_CONFIG: NBADailyRevealConfig = {
  sport: 'NBA',
  mode: 'prop_based',
  packSize: 3,
  weekStartsOn: 'MONDAY',
  timezone: US_EASTERN_TIMEZONE,
  allowPickChangeBeforeLock: true,
  fallbackManualPropsEnabled: true,
  days: [
    {
      dayNumber: 1,
      dayOfWeek: 'MONDAY',
      categoryKey: 'points_king',
      categoryName: 'Points King',
      propStatType: 'points',
      operator: 'over_or_equal',
      defaultThresholdLabel: '20+ points',
      openTimeEt: '00:00',
    },
    {
      dayNumber: 2,
      dayOfWeek: 'TUESDAY',
      categoryKey: 'rebound_king',
      categoryName: 'Rebound King',
      propStatType: 'rebounds',
      operator: 'over_or_equal',
      defaultThresholdLabel: '8+ rebounds',
      openTimeEt: '00:00',
    },
    {
      dayNumber: 3,
      dayOfWeek: 'WEDNESDAY',
      categoryKey: 'threes_king',
      categoryName: 'Three King',
      propStatType: 'three_points_made',
      operator: 'over_or_equal',
      defaultThresholdLabel: '3+ threes made',
      openTimeEt: '00:00',
    },
    {
      dayNumber: 4,
      dayOfWeek: 'THURSDAY',
      categoryKey: 'assist_king',
      categoryName: 'Assist King',
      propStatType: 'assists',
      operator: 'over_or_equal',
      defaultThresholdLabel: '6+ assists',
      openTimeEt: '00:00',
    },
    {
      dayNumber: 5,
      dayOfWeek: 'FRIDAY',
      categoryKey: 'combo_king',
      categoryName: 'Combo King',
      propStatType: 'points_rebounds',
      operator: 'over_or_equal',
      defaultThresholdLabel: '25+ points + rebounds',
      openTimeEt: '00:00',
    },
    {
      dayNumber: 6,
      dayOfWeek: 'SATURDAY',
      categoryKey: 'double_double_king',
      categoryName: 'Double-Double King',
      propStatType: 'double_double',
      /*
       * Yes/No market: DK prices "Double-Double" with no line at all, so there is no threshold
       * to clear. `over_or_true` is the operator the platform already uses for that shape
       * (NFL anytime touchdown, MLB home run) and grades on the settled value being >= 1.
       */
      operator: 'over_or_true',
      defaultThresholdLabel: 'Record a double-double',
      openTimeEt: '00:00',
    },
    {
      dayNumber: 7,
      dayOfWeek: 'SUNDAY',
      /*
       * Sunday repeats Monday's Points category. It needs its own category KEY because
       * `getDailyRevealPropCategoryByKey` resolves a key to a single category, and two days
       * sharing `points_king` would make day 7 indistinguishable from day 1. The player-facing
       * NAME is deliberately identical — to a user this is the same Points King round.
       */
      categoryKey: 'points_king_finale',
      categoryName: 'Points King',
      propStatType: 'points',
      operator: 'over_or_equal',
      defaultThresholdLabel: '20+ points',
      openTimeEt: '00:00',
    },
  ],
};

/** Look up a day's category config by 1-based day number. */
export function getNbaDayConfigByNumber(dayNumber: number): NBADailyRevealDayConfig | null {
  return NBA_DAILY_REVEAL_CONFIG.days.find(d => d.dayNumber === dayNumber) ?? null;
}

/** Look up a day's category config by category key, e.g. `rebound_king`. */
export function getNbaDayConfigByCategory(categoryKey: string): NBADailyRevealDayConfig | null {
  return NBA_DAILY_REVEAL_CONFIG.days.find(d => d.categoryKey === categoryKey) ?? null;
}

/**
 * Return the Monday that starts the NBA week containing `gameDate`.
 * NBA weeks run Monday → Sunday, matching the day cadence above.
 */
export function getNbaWeekStartMonday(gameDate: string): string {
  const d = new Date(`${gameDate}T12:00:00Z`);
  const daysSinceMonday = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  return d.toISOString().split('T')[0];
}
